import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './MobileMenu.css';
import { FiX, FiChevronDown, FiChevronUp, FiUser } from 'react-icons/fi';

// --- Assets ---
import IconCap2 from '../../../assets/icons/h2.svg';
import IconCap4 from '../../../assets/icons/h4.svg';
import IconCap8 from '../../../assets/icons/h8.svg';

// --- Tipados ---
type NestedSubMenuItem = {
  name: string;
  link?: string;
};

type SubMenuItem = {
  name: string;
  link?: string;
  subItems?: NestedSubMenuItem[];
  capacity?: number;
};

type MenuItem = {
  name: string;
  link?: string;
  subItems?: SubMenuItem[];
  isButton?: boolean;
  hasIcon?: boolean;
  onClick?: (e: React.MouseEvent) => void;
};

interface MobileMenuProps {
  isOpen: boolean;
  toggleMenu: () => void;
  onReserveClick: () => void;
  onLoginClick: () => void; // <--- Prop añadida para resolver el error de TS
}

interface CapacityIconProps {
  capacity?: number;
}

const CapacityIcon: React.FC<CapacityIconProps> = ({ capacity }) => {
  if (!capacity) return null;

  const iconMap: Record<number, string> = {
    2: IconCap2,
    4: IconCap4,
    8: IconCap8,
  };

  const selectedIcon = iconMap[capacity];
  if (!selectedIcon) return null;

  return (
    <img
      src={selectedIcon}
      alt={`Capacidad ${capacity}`}
      className="capacity-svg-icon"
      style={{ width: '20px', height: 'auto', marginLeft: '10px' }}
    />
  );
};

const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, toggleMenu, onReserveClick, onLoginClick }) => {
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const [openNestedSubMenu, setOpenNestedSubMenu] = useState<string | null>(null);

  const { t, i18n } = useTranslation();
  const currentLang = i18n.language.split('-')[0];

  const menuItemsFromJson = t('mobileMenu:items', { returnObjects: true }) as MenuItem[] | undefined;
  const safeMenuItems = Array.isArray(menuItemsFromJson) ? menuItemsFromJson : [];

  // Datos finales del menú con lógica de clicks integrada
  const menuData: MenuItem[] = [
    {
      name: t('mobileMenu:reserve'),
      link: '#',
      isButton: true,
      onClick: (e) => {
        e.preventDefault();
        onReserveClick();
        toggleMenu();
      }
    },
    {
      name: t('mobileMenu:login'),
      link: '#',
      isButton: true,
      hasIcon: true,
      onClick: (e) => { // <--- Lógica añadida para el botón de login
        e.preventDefault();
        onLoginClick();
        toggleMenu();
      }
    },
    ...safeMenuItems
  ];

  const handleSubMenuClick = (itemName: string) => {
    setOpenSubMenu(openSubMenu === itemName ? null : itemName);
  };

  const handleNestedSubMenuClick = (nestedItemName: string) => {
    setOpenNestedSubMenu(openNestedSubMenu === nestedItemName ? null : nestedItemName);
  };

  const getLocalizedLink = (link?: string) => {
    if (!link || link === '#') return '#';
    if (link.startsWith(`/${currentLang}`)) return link;
    return `/${currentLang}${link}`;
  };

  return (
    <>
      <div className={`mobile-menu-overlay ${isOpen ? 'open' : ''}`} onClick={toggleMenu}></div>
      <div className={`mobile-menu-panel ${isOpen ? 'open' : ''}`}>
        <button onClick={toggleMenu} className="close-menu-button"><FiX size={30} /></button>

        <nav className="mobile-nav">
          <ul>
            {menuData.map((item) => (
              <li key={item.name}
                className={`
                  ${item.subItems ? 'has-submenu' : ''} 
                  ${item.isButton ? 'is-button' : ''}
                  ${item.name === t('mobileMenu:reserve') ? 'mobile-only-button-tablet' : ''}
                  ${item.name === t('mobileMenu:login') ? 'mobile-only-button-mobile' : ''}
                `}
              >
                {item.subItems ? (
                  <div className="anim-box submenu-toggle" onClick={() => handleSubMenuClick(item.name)}>
                    <span>{item.name}</span>
                    {openSubMenu === item.name ? <FiChevronUp /> : <FiChevronDown />}
                    <span className="line line-top"></span><span className="line line-bottom"></span>
                  </div>
                ) : (
                  <Link
                    to={item.isButton ? item.link! : getLocalizedLink(item.link)}
                    className="anim-box"
                    onClick={(e) => {
                      if (item.onClick) {
                        item.onClick(e);
                      } else {
                        toggleMenu();
                      }
                    }}
                  >
                    {item.hasIcon && <FiUser size={18} style={{ marginRight: '8px' }} />}
                    <span>{item.name}</span>
                    <span className="line line-top"></span>
                    <span className="line line-bottom"></span>
                  </Link>
                )}

                {item.subItems && (
                  <ul className={`submenu ${openSubMenu === item.name ? 'open' : ''}`}>
                    {item.subItems.map((subItem) => (
                      <li key={subItem.name} className={subItem.subItems ? 'has-submenu' : ''}>
                        {subItem.subItems ? (
                          <div className="anim-box nested-submenu-toggle" onClick={() => handleNestedSubMenuClick(subItem.name)}>
                            <span>{subItem.name}</span>
                            {openNestedSubMenu === subItem.name ? <FiChevronUp /> : <FiChevronDown />}
                            <span className="line line-top"></span><span className="line line-bottom"></span>
                          </div>
                        ) : (
                          <Link to={getLocalizedLink(subItem.link)} className="anim-box link-with-icon" onClick={toggleMenu}>
                            <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                              <span>{subItem.name}</span>
                              <CapacityIcon capacity={subItem.capacity} />
                            </div>
                            <span className="line line-top"></span><span className="line line-bottom"></span>
                          </Link>
                        )}

                        {subItem.subItems && (
                          <ul className={`submenu submenu-level-2 ${openNestedSubMenu === subItem.name ? 'open' : ''}`}>
                            {subItem.subItems.map(nestedItem => (
                              <li key={nestedItem.name}>
                                <Link to={getLocalizedLink(nestedItem.link)} className="anim-box" onClick={toggleMenu}>
                                  {nestedItem.name}
                                  <span className="line line-top"></span>
                                  <span className="line line-bottom"></span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </>
  );
};

export default MobileMenu;